/**
 * Normalizes API chart rows for Recharts bar widgets.
 * Stacked grouped (category × segment) bars use long rows: { name, segment, value }.
 */

const STACKED_TOP_CATEGORIES = 10;

function findKey(row, candidates) {
  if (!row || typeof row !== 'object') return null;
  const keys = Object.keys(row);
  const lower = keys.map((k) => [k, k.toLowerCase()]);
  for (const cand of candidates) {
    const c = cand.toLowerCase();
    const hit = lower.find(([, lk]) => lk === c);
    if (hit) return hit[0];
  }
  return null;
}

/**
 * If data is stacked long format from the API, pivot to wide rows + segment keys (matches BuildBarChart).
 * @returns {{ isStacked: boolean, chartData: array, segmentKeys: string[] }}
 */
export function pivotStackedBarLongRows(data) {
  if (!Array.isArray(data) || data.length === 0) {
    return { isStacked: false, chartData: data || [], segmentKeys: [] };
  }
  const r0 = data[0];
  if (!r0 || typeof r0 !== 'object') {
    return { isStacked: false, chartData: data, segmentKeys: [] };
  }
  const nameKey = findKey(r0, ['name', 'category', 'label']);
  const segKey = findKey(r0, ['segment']);
  const valKey = findKey(r0, ['value', 'count']);
  if (!nameKey || !segKey || !valKey) {
    return { isStacked: false, chartData: data, segmentKeys: [] };
  }

  const segmentsOrdered = [];
  const seenSeg = new Set();
  for (const row of data) {
    const s = String(row[segKey] ?? '').trim() || '(blank)';
    if (!seenSeg.has(s)) {
      seenSeg.add(s);
      segmentsOrdered.push(s);
    }
  }
  segmentsOrdered.sort((a, b) => a.localeCompare(b));

  const byCategory = new Map();
  for (const row of data) {
    const cat = String(row[nameKey] ?? '').trim() || 'Unknown';
    const seg = String(row[segKey] ?? '').trim() || '(blank)';
    const val = Number(row[valKey]);
    const n = Number.isFinite(val) ? val : 0;
    if (!byCategory.has(cat)) {
      const o = { name: cat };
      segmentsOrdered.forEach((s) => {
        o[s] = 0;
      });
      byCategory.set(cat, o);
    }
    const o = byCategory.get(cat);
    o[seg] = (o[seg] || 0) + n;
  }

  const totalOf = (row) =>
    segmentsOrdered.reduce((sum, s) => sum + (Number(row[s]) || 0), 0);

  let chartData = Array.from(byCategory.values());
  chartData.sort((a, b) => totalOf(b) - totalOf(a));
  if (chartData.length > STACKED_TOP_CATEGORIES) {
    chartData = chartData.slice(0, STACKED_TOP_CATEGORIES);
  }

  return { isStacked: true, chartData, segmentKeys: segmentsOrdered };
}
